import React, { useState, useEffect } from "react";
import AppWrapper from "../AppWrapper";
import {
  BlockStack,
  Card,
  Layout,
  Page,
  Spinner,
  Text,
  Checkbox,
  Toast,
  Frame,
  Select,
  Banner,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { useParams } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";

function EditUser() {
  const { userId } = useParams();
  // Only a superadmin may grant or revoke permissions (enforced in the DB:
  // 20261030_user_permissions_superadmin_only.sql).
  const { claims } = UserAuth();
  const canEditPermissions = claims?.role === "superadmin";
  const [permError, setPermError] = useState(null);

  const [user, setUser] = useState(undefined);
  const [userPermissions, setUserPermissions] = useState(undefined); // original from DB
  const [updatedUserPermissions, setUpdatedUserPermissions] =
    useState(undefined); // modified in UI
  const [availablePermissions, setAvailablePermissions] = useState(undefined);
  const [toastActive, setToastActive] = useState(false);
  const [toastText, setToastText] = useState("Permisos guardados");
  const [branches, setBranches] = useState([]);
  const [branchError, setBranchError] = useState(null);
  const toggleToastActive = () => setToastActive((a) => !a);

  // === Fetch Data ===
  useEffect(() => {
    const fetchData = async () => {
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .single();
      if (userError) return console.error(userError);

      const { data: upData, error: upError } = await supabase
        .from("user_permissions")
        .select("id, permission_id")
        .eq("user_id", userId);
      if (upError) return console.error(upError);

      const { data: permissionData, error: permissionError } = await supabase
        .from("permissions")
        .select("*")
        .order("code", { ascending: true });
      if (permissionError) return console.error(permissionError);

      const { data: branchData } = await supabase
        .from("branches")
        .select("id, name")
        .eq("is_active", true)
        .eq("segment", "corto_plazo")
        .order("name");

      setUser(userData);
      setBranches(branchData || []);
      setUserPermissions(upData);
      setUpdatedUserPermissions(upData);
      setAvailablePermissions(permissionData);
    };
    if (!user) fetchData();
  }, []);

  // === Helpers ===
  const hasPermission = (permId) =>
    updatedUserPermissions?.some((p) => p.permission_id === permId);

  const handlePermissionChange = (permId, checked) => {
    setUpdatedUserPermissions((prev) => {
      if (checked) {
        if (prev.some((p) => p.permission_id === permId)) return prev;
        return [...prev, { permission_id: permId }];
      } else {
        return prev.filter((p) => p.permission_id !== permId);
      }
    });
  };

  const permissionsChanged = () => {
    if (!userPermissions || !updatedUserPermissions) return false;
    const originalIds = userPermissions.map((p) => p.permission_id).sort();
    const updatedIds = updatedUserPermissions
      .map((p) => p.permission_id)
      .sort();
    return JSON.stringify(originalIds) !== JSON.stringify(updatedIds);
  };

  // === Save handler ===
  const handleSave = async () => {
    const originalIds = userPermissions.map((p) => p.permission_id);
    const updatedIds = updatedUserPermissions.map((p) => p.permission_id);

    const toAdd = updatedIds.filter((id) => !originalIds.includes(id));
    const toRemove = originalIds.filter((id) => !updatedIds.includes(id));

    setPermError(null);
    try {
      // Remove unselected
      if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from("user_permissions")
          .delete()
          .in("permission_id", toRemove)
          .eq("user_id", userId);
        if (deleteError) throw deleteError;
      }

      // Add new ones (using upsert for safety)
      if (toAdd.length > 0) {
        const inserts = toAdd.map((id) => ({
          user_id: userId,
          permission_id: id,
        }));
        const { error: insertError } = await supabase
          .from("user_permissions")
          .upsert(inserts, { onConflict: "user_id,permission_id" });
        if (insertError) throw insertError;
      }

      // Update local state and show success
      setUserPermissions(updatedUserPermissions);
      setToastText("Permisos guardados");
      setToastActive(true);
    } catch (error) {
      console.error(error);
      setPermError(error.message || "No se pudieron guardar los permisos");
    }
  };

  // Sucursal (null = todas): which open washes they see in the Check app and
  // which sucursal their washes count for. Saved right away.
  const handleBranchChange = async (value) => {
    const branchId = value === "" ? null : Number(value);
    setBranchError(null);
    const { error } = await supabase.rpc("set_user_branch", {
      p_user: userId,
      p_branch: branchId,
    });
    if (error) return setBranchError(error.message);
    setUser((u) => ({ ...u, branch_id: branchId }));
    setToastText("Sucursal guardada");
    setToastActive(true);
  };

  // === UI Components ===
  const pageContent = () => {
    if (!user || !userPermissions || !availablePermissions)
      return <Spinner size="small" />;

    return (
      <BlockStack gap="300">
        <Card>
          <Text variant="headingMd">{user.name}</Text>
          <Text>{user.email}</Text>
          <Text tone="subdued">{user.id}</Text>
        </Card>

        <Card>
          <BlockStack gap="200">
            {branchError && <Banner tone="critical">{branchError}</Banner>}
            <Select
              label="Sucursal"
              helpText="En la app Check ve los lavados en curso de su sucursal; con Todas, los de todas."
              options={[
                { label: "Todas", value: "" },
                ...branches.map((b) => ({ label: b.name, value: String(b.id) })),
              ]}
              value={user.branch_id == null ? "" : String(user.branch_id)}
              onChange={handleBranchChange}
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text variant="headingMd">Permisos</Text>
            {!canEditPermissions && <Text tone="subdued">Solo un superadmin puede cambiar permisos.</Text>}
            {permError && <Banner tone="critical">{permError}</Banner>}
            <BlockStack gap="100">
              {availablePermissions.map((perm) => (
                <Checkbox
                  key={perm.id}
                  label={<Text>{perm.code}</Text>}
                  helpText={perm.description}
                  checked={hasPermission(perm.id)}
                  disabled={!canEditPermissions}
                  onChange={(checked) =>
                    handlePermissionChange(perm.id, checked)
                  }
                />
              ))}
            </BlockStack>
          </BlockStack>
        </Card>
      </BlockStack>
    );
  };

  const saveButton = {
    content: "Guardar",
    disabled: !canEditPermissions || !permissionsChanged(),
    onAction: handleSave,
  };

  // === Render ===
  return (
    <AppWrapper>
      <Frame>
        <Page
          title="Editar Usuario"
          primaryAction={saveButton}
          backAction={{ url: "/users" }}
        >
          {pageContent()}
        </Page>
        {toastActive && (
          <Toast content={toastText} onDismiss={toggleToastActive} />
        )}
      </Frame>
    </AppWrapper>
  );
}

export default EditUser;
